import { SupabaseClient } from '@supabase/supabase-js';
import type { Transaction, Account, Category, Budget, Tag, Payee, SavingsGoal } from '@flows.cash/types';

type TableTypes = {
  transactions: Transaction;
  accounts: Account;
  categories: Category;
  budgets: Budget;
  tags: Tag;
  payees: Payee;
  savings_goals: SavingsGoal;
}

/**
 * Options for paginated queries
 */
export interface PaginationOptions {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

/**
 * Filter options for queries
 */
export interface FilterOptions {
  field: string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is';
  value: any;
}

/**
 * Build a paginated and filtered query for a Supabase table
 */
export function buildOptimizedQuery<T extends keyof TableTypes>(
  supabaseClient: SupabaseClient,
  table: T,
  columns: string = '*',
  pagination: PaginationOptions = {},
  filters: FilterOptions[] = [],
  userId?: string
): any {
  const { 
    page = 1, 
    pageSize = 20, 
    orderBy = 'created_at', 
    orderDirection = 'desc' 
  } = pagination;
  
  // Calculate range for pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  
  // Start building the query
  let query = supabaseClient
    .from(table)
    .select(columns, { count: 'exact' });
  
  // Add user ID filter if provided
  if (userId) {
    query = query.eq('user_id', userId);
  }
  
  // Apply all filters
  filters.forEach(filter => {
    const { field, operator, value } = filter;
    
    switch (operator) {
      case 'eq':
        query = query.eq(field, value);
        break;
      case 'neq':
        query = query.neq(field, value);
        break;
      case 'gt':
        query = query.gt(field, value);
        break;
      case 'gte':
        query = query.gte(field, value);
        break;
      case 'lt':
        query = query.lt(field, value);
        break;
      case 'lte':
        query = query.lte(field, value);
        break;
      case 'like':
        query = query.like(field, value);
        break;
      case 'ilike':
        query = query.ilike(field, value);
        break;
      case 'in':
        query = query.in(field, value);
        break;
      case 'is':
        query = query.is(field, value);
        break;
    }
  });
  
  // Order and paginate the results
  return query
    .order(orderBy, { ascending: orderDirection === 'asc' })
    .range(start, end);
}

/**
 * Execute a query and return paginated results with count
 */
export async function executePagedQuery<T>(query: any) {
  const { data, error, count } = await query;
  
  if (error) {
    throw error;
  }
  
  return {
    data: data as T[],
    count: count || 0,
    error: null
  };
}

/**
 * Optimizes bulk inserts by chunking data when needed
 */
export async function bulkInsert<T extends keyof TableTypes>(
  supabaseClient: SupabaseClient,
  table: T,
  records: Partial<TableTypes[T]>[],
  chunkSize: number = 1000
): Promise<{
  data: TableTypes[T][] | null;
  error: Error | null;
}> {
  try {
    // If the data is small enough, do a single insert
    if (records.length <= chunkSize) {
      const { data, error } = await supabaseClient.from(table).insert(records).select();
      if (error) throw error;
      return { data, error: null };
    }
    
    // Otherwise, chunk the data
    const chunks: Partial<TableTypes[T]>[][] = [];
    for (let i = 0; i < records.length; i += chunkSize) {
      chunks.push(records.slice(i, i + chunkSize));
    }
    
    // Process chunks sequentially
    const results: TableTypes[T][] = [];
    for (const chunk of chunks) {
      const { data, error } = await supabaseClient.from(table).insert(chunk).select();
      if (error) throw error;
      if (data) results.push(...data);
    }
    
    return { data: results, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
} 